<template>
  <div class="col-md-12 mb-6">
    <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
      <label class="fs-6 fw-bold form-label mb-0">
        {{ attribute.label }} <span class="text-danger" v-if="attribute.required">*</span>
      </label>
      <div class="d-flex gap-2">
        <button
          v-if="canAnalyze"
          type="button"
          class="btn btn-sm btn-light"
          title="Ver o que se repete nos registros deste campo"
          @click="insightsOpen = true">
          <i class="bi bi-pie-chart"></i> Análise
        </button>
        <button type="button" class="btn btn-sm btn-light-success" :disabled="disabled" @click="openModal(null)">
          <i class="bi bi-plus-lg"></i> {{ addLabel }}
        </button>
      </div>
    </div>

    <RecordInsightsDrawer
      :is-open="insightsOpen"
      :professional-id="schedule?.id_prof"
      :patient-id="schedule?.id_patient"
      :place-id="schedule?.id_place"
      :label="attribute.label"
      @close="insightsOpen = false"/>

    <div v-if="items.length === 0" class="text-muted fs-7 py-3">
      Nenhum item adicionado.
    </div>

    <div v-else class="structured-list d-flex flex-column gap-2">
      <div v-for="(item, index) in items"
           :key="index"
           class="item-card d-flex justify-content-between align-items-start gap-3">
        <div class="flex-grow-1 min-w-0">
          <div class="fw-semibold fs-7 text-gray-800">{{ headline(item) }}</div>
          <div class="text-muted fs-8 mt-1" v-if="details(item)">{{ details(item) }}</div>
        </div>
        <div class="d-flex align-items-center gap-1 flex-shrink-0" v-if="!disabled">
          <button type="button"
                  class="btn btn-icon btn-sm btn-active-light-primary"
                  :aria-label="'Editar ' + headline(item)"
                  @click="openModal(index)">
            <i class="bi bi-pencil"></i>
          </button>
          <button type="button"
                  class="btn btn-icon btn-sm btn-active-light-danger"
                  :aria-label="'Remover ' + headline(item)"
                  @click="removeItem(index)">
            <i class="bi bi-trash"></i>
          </button>
        </div>
      </div>
    </div>

    <Teleport to="body">
<div class="modal fade" tabindex="-1" :id="modalId" ref="modalEl">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <form method="post" @submit.prevent="submit(false)">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">{{ editingIndex === null ? addLabel : 'Editar item' }}</h5>
              <div class="btn btn-icon btn-sm btn-active-light-primary ms-2" data-bs-dismiss="modal" aria-label="Fechar">
                <i class="bi bi-x-lg"></i>
              </div>
            </div>

            <div class="modal-body">
              <div class="row g-4">
                <div :class="field.columnClass" v-for="(field, position) in fields" :key="field.key">
                  <label class="fs-7 fw-semibold form-label text-gray-700" :for="`${modalId}-${field.key}`">
                    {{ field.label }}
                    <span class="text-danger" v-if="field.required">*</span>
                  </label>

                  <select v-if="field.type === 'select' && field.options.length > 12"
                          class="form-select form-select-solid"
                          :id="`${modalId}-${field.key}`"
                          :ref="position === 0 ? 'firstField' : undefined"
                          v-model="form[field.key]">
                    <option value="">--Selecione--</option>
                    <option v-for="option in field.options" :key="option" :value="option">{{ option }}</option>
                  </select>

                  <div v-else-if="field.type === 'select'" class="d-flex flex-wrap gap-2">
                    <button
                      v-for="option in field.options"
                      :key="option"
                      type="button"
                      class="btn btn-sm"
                      :class="form[field.key] === option ? 'btn-primary' : 'btn-light-primary'"
                      @click="form[field.key] = option">
                      {{ option }}
                    </button>
                  </div>

                  <textarea v-else-if="field.type === 'textarea'"
                            class="form-control form-control-solid"
                            :id="`${modalId}-${field.key}`"
                            :ref="position === 0 ? 'firstField' : undefined"
                            :rows="field.rows"
                            :placeholder="field.placeholder"
                            v-model="form[field.key]"></textarea>

                  <input v-else
                         :type="inputType(field)"
                         class="form-control form-control-solid"
                         :id="`${modalId}-${field.key}`"
                         :ref="position === 0 ? 'firstField' : undefined"
                         :placeholder="field.placeholder"
                         v-model="form[field.key]">
                </div>
              </div>
            </div>

            <div class="modal-footer justify-content-between">
              <span class="text-danger fs-8" v-if="formError">{{ formError }}</span>
              <span v-else></span>
              <span class="d-flex gap-2">
                <button type="button" class="btn btn-light" data-bs-dismiss="modal">Cancelar</button>
                <button
                  v-if="editingIndex === null"
                  type="button"
                  class="btn btn-light-primary"
                  @click="submit(true)">
                  Salvar e novo
                </button>
                <button type="submit" class="btn btn-primary">Salvar</button>
              </span>
            </div>
          </div>
        </form>
      </div>
    </div>
  </Teleport>
  </div>
</template>

<script>
// Swal e global, vindo do bundle do tema -- o pacote sweetalert2 nao e
// dependencia direta do projeto.
/* global Swal */

import RecordInsightsDrawer from './RecordInsightsDrawer.vue';
import {normalizeFields, missingRequired} from './structured-list-fields';

export default {
  name: "StructuredListInput",
  components: {RecordInsightsDrawer},
  emits: ['onChange'],
  props: {
    attribute: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    schedule: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      insightsOpen: false,
      items: [],
      editingIndex: null,
      form: {},
      formError: '',
    };
  },
  computed: {
    options() {
      return this.attribute?.options ?? {};
    },
    fields() {
      return normalizeFields(this.options?.fields);
    },
    addLabel() {
      return this.options?.add_label ?? 'Adicionar item';
    },
    modalId() {
      return `structured_list_modal_${this.attribute.id_medrec_attr}`;
    },
    canAnalyze() {
      return !!(this.schedule?.id_prof && this.schedule?.id_patient);
    },
  },
  created() {
    this.items = this.parseItems();
  },
  mounted() {
    this.$refs.modalEl.addEventListener('shown.bs.modal', () => {
      const first = this.$refs.firstField;
      const el = Array.isArray(first) ? first[0] : first;
      if (el) {
        el.focus();
      }
    });
  },
  watch: {
    'attribute.text': {
      handler() {
        this.items = this.parseItems();
      },
    },
  },
  methods: {
    parseItems() {
      if (!this.attribute?.text) {
        return Array.isArray(this.options?.defaults) ? this.options.defaults : [];
      }

      if (typeof this.attribute.text === 'string') {
        try {
          return JSON.parse(this.attribute.text);
        } catch (_) {
          return [];
        }
      }

      return Array.isArray(this.attribute.text) ? this.attribute.text : [];
    },

    /**
     * O componente e generico: nao sabe qual campo e o "titulo" do item.
     * O primeiro campo preenchido vira o destaque, o resto vira detalhe.
     */
    headline(item) {
      const filled = this.fields.filter((field) => this.hasValue(item[field.key]));
      if (filled.length === 0) {
        return 'Item sem preenchimento';
      }

      return filled.slice(0, 2).map((field) => item[field.key]).join(' · ');
    },

    details(item) {
      const filled = this.fields.filter((field) => this.hasValue(item[field.key]));

      return filled
        .slice(2)
        .map((field) => `${field.label}: ${item[field.key]}`)
        .join(' · ');
    },

    hasValue(value) {
      return value !== null && value !== undefined && String(value).trim() !== '';
    },

    emptyForm() {
      return this.fields.reduce((acc, field) => {
        acc[field.key] = this.defaultValue(field);
        return acc;
      }, {});
    },

    openModal(index) {
      if (this.disabled) {
        return;
      }

      this.editingIndex = index;
      this.formError = '';
      this.form = index === null ? this.emptyForm() : { ...this.emptyForm(), ...this.items[index] };

      this.$nextTick(() => {
        window.bootstrap.Modal.getOrCreateInstance(document.getElementById(this.modalId)).show();
      });
    },

    inputType(field) {
      return ['number', 'time', 'date'].includes(field.type) ? field.type : 'text';
    },

    submit(saveAndNew = false) {
      const item = { ...this.form };

      // Item totalmente vazio nao vira linha na lista: so polui o plano.
      if (!this.fields.some((field) => this.hasValue(item[field.key]))) {
        this.formError = 'Preencha pelo menos um campo.';
        return;
      }

      const missing = missingRequired(this.fields, item);
      if (missing.length) {
        this.formError = `Preencha: ${missing.join(', ')}.`;
        return;
      }

      this.formError = '';

      if (this.editingIndex === null) {
        this.items.push(item);
      } else {
        this.items.splice(this.editingIndex, 1, item);
      }

      this.emitChange();

      if (saveAndNew && this.editingIndex === null) {
        this.form = this.emptyForm();
        return;
      }

      window.bootstrap.Modal.getOrCreateInstance(document.getElementById(this.modalId)).hide();
    },

    defaultValue(field) {
      if (field.type === 'time') {
        return this.currentTime();
      }

      if (field.type === 'date') {
        return new Date().toISOString().slice(0, 10);
      }

      return '';
    },

    currentTime() {
      const now = new Date();

      return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    },

    removeItem(index) {
      // Nao ha desfazer nesta lista: um clique errado perde o item.
      Swal.fire({
        title: 'Remover item?',
        text: this.headline(this.items[index]),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, remover',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-secundary',
        },
      }).then((result) => {
        if (!result.isConfirmed) {
          return;
        }

        this.items.splice(index, 1);
        this.emitChange();
      });
    },

    emitChange() {
      this.$emit('onChange', {
        ...this.attribute,
        text: JSON.stringify(this.items),
      });
    },
  },
};
</script>

<style scoped>
.item-card {
  border: 1px solid var(--bs-gray-300, #e4e6ef);
  border-radius: 8px;
  padding: 12px 14px;
  background: var(--bs-body-bg, #fff);
  transition: border-color .15s ease, background-color .15s ease;
}

.item-card:hover {
  border-color: var(--bs-gray-400, #b5b5c3);
  background: var(--bs-gray-100, #f9f9f9);
}

.min-w-0 {
  min-width: 0;
}

@media (prefers-reduced-motion: reduce) {
  .item-card {
    transition: none;
  }
}
</style>
